import type { AxiosError } from 'axios';
import i18next from 'i18next';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useShowToast } from '@/composables/useShowToast.composable';
import type { CursorPaginated } from '@/helpers/interfaces/cursor-paginated.interface';
import { axiosI } from '@/plugins';
import { misc } from '@/router/miscellaneous.routes.enum';
import type {
  CreateConversationType,
  CreateMessageType,
  Message,
  Thread,
} from '@/submodules/himam-common-components/src/helpers/interfaces/thread.interface';
import { ThreadStatus } from '@/submodules/himam-common-components/src/helpers/interfaces/thread.interface';
import { ApiHandler } from '@/submodules/postman-to-ts/apiHandler';

import { useOrganizationStore } from './organzation.pinia';

// Conversation error codes mapped to translation keys
const ConversationErrorTranslationKeys: Record<string, string> = {
  'conversation-001': 'conversationError.notFound',
  'conversation-002': 'conversationError.requiresRecipients',
  'conversation-003': 'conversationError.notPartOfConversation',
  'conversation-004': 'conversationError.invalidUserOrOrganization',
  'conversation-005': 'conversationError.recipientsListEmpty',
  'conversation-006': 'conversationError.insufficientPermissions',
  'conversation-007': 'conversationError.courseNotStarted',
  'conversation-008': 'conversationError.courseAlreadyEnded',
  'conversation-009': 'conversationError.userNotEnrolled',
  'conversation-010': 'conversationError.studentsMessageInstructorsOnly',
  'conversation-011': 'conversationError.userNotInstructor',
  'conversation-012': 'conversationError.instructorsMessageEnrolleesOrAdmins',
  'conversation-013': 'conversationError.adminsMessageInstructorsOnly',
  'conversation-014': 'conversationError.invalidRecipient',
  'conversation-015': 'conversationError.missingConversationContext',
  'conversation-016': 'conversationError.conversationNotBelongToCourse',
  'conversation-017': 'conversationError.courseNotFound',
};

// Helper function to handle conversation errors
const handleConversationError = (
  error: AxiosError<{
    errorCode?: string;
  }>
) => {
  const { t } = i18next;

  const translationKey =
    ConversationErrorTranslationKeys[error?.response?.data?.errorCode ?? ''] ??
    'conversationError.genericError';
  const message = t(translationKey, {
    courseText: useOrganizationStore().courseText,
  });
  useShowToast.error({ msg: message });
};

export const useConversationStore = defineStore('ConversationStore', () => {
  const route = useRoute();
  const router = useRouter();

  const unReadConversationCount = ref(0);
  const conversations: Ref<CursorPaginated<Thread>> = ref({
    data: [],
    nextCursor: undefined,
  });
  const messages: Ref<CursorPaginated<Message>> = ref({
    data: [],
    nextCursor: undefined,
  });
  const singleConversation: Ref<Thread | null> = ref(null);
  const lastCreatedConversationId: Ref<number | null> = ref(null);

  const getNextCursorFromList = <T extends { id: number }>(list: T[]) => {
    if (!list.length) return undefined;
    return list[list.length - 1].id;
  };

  const getSingleConversationById = async (conversationId: number) => {
    const conversationIndex = conversations.value.data.findIndex(
      (thread) => thread.id === conversationId
    );
    if (conversationIndex !== -1)
      singleConversation.value = conversations.value.data[conversationIndex];
    try {
      const { path, method } = ApiHandler.messages.getSingleConversation;
      const { data } = await axiosI<Thread>(path({ conversationId }), {
        method,
      });
      singleConversation.value = data;
      if (conversationIndex !== -1) {
        const updatedConversations = [...conversations.value.data];
        updatedConversations[conversationIndex] = data;
        conversations.value = {
          data: updatedConversations,
          nextCursor: conversations.value.nextCursor,
        };
      }
    } catch (error) {
      singleConversation.value = null;
      const e = error as AxiosError<{
        errorCode?: string;
      }>;
      handleConversationError(e);
    }
  };
  const getAllConversations = async (
    params: {
      cursor?: number;
      take: number;
      status: ThreadStatus;
      courseId?: number;
    },
    options?: { append?: boolean }
  ) => {
    try {
      const { path, method } = ApiHandler.messages.allConversations;
      const { data } = await axiosI<CursorPaginated<Thread>>(path(), {
        method,
        params,
      });

      if (options?.append) {
        const existingIds = new Set(
          conversations.value.data.map((thread) => thread.id)
        );
        const appendedThreads = data.data.filter(
          (thread) => !existingIds.has(thread.id)
        );
        conversations.value = {
          data: [...conversations.value.data, ...appendedThreads],
          nextCursor: data.nextCursor,
        };
      } else {
        conversations.value = data;
      }
      return data;
    } catch (error) {
      const e = error as AxiosError<{
        errorCode?: string;
      }>;
      handleConversationError(e);
    }
  };
  const getUnreadConversationCount = async () => {
    try {
      const { path, method } = ApiHandler.messages.getUnreadConversationCount;
      const { data } = await axiosI<number>(path(), {
        method,
      });
      unReadConversationCount.value = data;
    } catch (error) {
      const e = error as AxiosError<{
        errorCode?: string;
      }>;
      handleConversationError(e);
    }
  };
  const markAsStarred = async (conversationId: number, isStarred: boolean) => {
    try {
      const { path, method } = ApiHandler.messages.markAsStarred;
      await axiosI(path({ conversationId }), {
        method,
        data: {
          isStarred,
        },
      });
      if (singleConversation.value?.id === conversationId) {
        singleConversation.value.userConversationStates =
          singleConversation.value.userConversationStates.map((state) => ({
            ...state,
            isStarred,
          }));
      }
      conversations.value.data = conversations.value.data.map((thread) => {
        if (thread.id === conversationId) {
          return {
            ...thread,
            userConversationStates: thread.userConversationStates.map(
              (state) => ({
                ...state,
                isStarred,
              })
            ),
          };
        }
        return thread;
      });
    } catch (error) {
      const e = error as AxiosError<{
        errorCode?: string;
      }>;
      handleConversationError(e);
    }
  };
  const markAsRead = async (conversationId: number) => {
    try {
      const { path, method } = ApiHandler.messages.markAsRead;
      await axiosI(path({ conversationId }), {
        method,
      });
      getUnreadConversationCount();
      if (singleConversation.value?.id === conversationId) {
        singleConversation.value = {
          ...singleConversation.value,
          userConversationStates:
            singleConversation.value.userConversationStates.map((state) => ({
              ...state,
              isRead: true,
            })),
          messages: singleConversation.value.messages.map((message) => ({
            ...message,
            isRead: true,
          })),
        };
      }
      conversations.value = {
        data: conversations.value.data.map((thread) => {
          if (thread.id !== conversationId) return thread;
          return {
            ...thread,
            userConversationStates: thread.userConversationStates.map(
              (state) => ({
                ...state,
                isRead: true,
              })
            ),
            messages: thread.messages.map((message) => ({
              ...message,
              isRead: true,
            })),
          };
        }),
        nextCursor: conversations.value.nextCursor,
      };
      if (conversationId === +route.params.conversationId) {
        messages.value = {
          data: messages.value.data.map((message) => ({
            ...message,
            isRead: true,
          })),
          nextCursor: messages.value.nextCursor,
        };
      }
    } catch (error) {
      const e = error as AxiosError<{
        errorCode?: string;
      }>;
      handleConversationError(e);
    }
  };
  const composeMessage = async (
    payload: CreateConversationType,
    // When called from outside the messages section (e.g. the course overview
    // "Send Message" button), pass skipRedirect so the user stays on their
    // current page and can be shown a success toaster instead of being
    // navigated straight into the new conversation.
    options?: { skipRedirect?: boolean }
  ) => {
    const { path, method } = ApiHandler.messages.composeConversation;
    try {
      const { data: thread } = await axiosI<Thread>(path(), {
        method,
        data: payload,
      });
      const updatedConversations = [thread, ...conversations.value.data];
      conversations.value = {
        data: updatedConversations,
        nextCursor: getNextCursorFromList(updatedConversations),
      };
      if (!route?.params?.conversationId) {
        const initialMessages = thread.messages;
        singleConversation.value = {
          ...thread,
          messages: initialMessages,
        };
        messages.value = {
          data: [...initialMessages],
          nextCursor: getNextCursorFromList(initialMessages),
        };
        if (!options?.skipRedirect) {
          router.replace({
            name: misc.singleConversation.name,
            params: {
              conversationId: thread.id,
            },
          });
        }
      }
      lastCreatedConversationId.value = thread.id;
      return thread;
    } catch (error) {
      const e = error as AxiosError<{
        errorCode?: string;
      }>;
      handleConversationError(e);
    }
  };
  const getMessagesByConversationId = async (conversationId: number) => {
    try {
      const { path, method } = ApiHandler.messages.getMessagesByConversationId;
      const { data } = await axiosI<CursorPaginated<Message>>(
        path({ conversationId }),
        {
          method,
          params: {
            take: Number.MAX_SAFE_INTEGER,
          },
        }
      );
      messages.value = data;
    } catch (error) {
      messages.value = { data: [], nextCursor: undefined };
      const e = error as AxiosError<{
        errorCode?: string;
      }>;
      handleConversationError(e);
    }
  };
  const sendMessage = async (
    conversationId: number,
    payload: CreateMessageType
  ) => {
    try {
      const { path, method } = ApiHandler.messages.messageReply;
      const { data } = await axiosI<Message>(path({ conversationId }), {
        method,
        data: payload,
      });
      if (singleConversation.value?.id === conversationId) {
        singleConversation.value.messages = [data];
        singleConversation.value.updatedAt = data.createdAt;
        const updatedMessages = [data, ...messages.value.data];
        messages.value = {
          data: updatedMessages,
          nextCursor: getNextCursorFromList(updatedMessages),
        };
      }
      const conversationIndex = conversations.value.data.findIndex(
        (conv) => conv.id === conversationId
      );
      if (conversationIndex !== -1) {
        const updatedConversation: Thread = {
          ...conversations.value.data[conversationIndex],
          updatedAt: data.createdAt,
          messages: [data],
        };
        const remainingConversations = conversations.value.data.filter(
          (conv) => conv.id !== conversationId
        );
        const updatedConversations = [
          updatedConversation,
          ...remainingConversations,
        ];
        conversations.value = {
          data: updatedConversations,
          nextCursor: getNextCursorFromList(updatedConversations),
        };
      }
      return data;
    } catch (error) {
      const e = error as AxiosError<{
        errorCode?: string;
      }>;
      handleConversationError(e);
    }
  };

  const receivedMessageHandler = (conversation: Thread) => {
    // update conversations
    const remainingConversations = conversations.value.data.filter(
      ({ id }) => id != conversation.id
    );
    const updatedConversations = [conversation, ...remainingConversations];
    conversations.value = {
      data: updatedConversations,
      nextCursor: getNextCursorFromList(updatedConversations),
    };

    // update single conversation
    if (
      singleConversation.value &&
      singleConversation.value.id === conversation.id
    ) {
      singleConversation.value = conversation;
      const mergedMessages = [...conversation.messages, ...messages.value.data];
      messages.value = {
        data: mergedMessages,
        nextCursor: getNextCursorFromList(mergedMessages),
      };
    }
  };
  const clearLastCreatedConversationId = () => {
    lastCreatedConversationId.value = null;
  };
  return {
    unReadConversationCount,
    getAllConversations,
    getUnreadConversationCount,
    markAsStarred,
    markAsRead,
    getMessagesByConversationId,
    sendMessage,
    conversations,
    messages,
    singleConversation,
    composeMessage,
    getSingleConversationById,
    receivedMessageHandler,
    lastCreatedConversationId,
    clearLastCreatedConversationId,
  };
});
